import type {
  ProtoPayroll,
  ProtoPayrollUser,
} from "@switchsystems/coactive-rota-lib";

import "pinia";
declare module "pinia" {
  export interface PiniaCustomStateProperties {
    $api: PiniaApiHelper;
  }
}

export interface HubEvent {
  event: string;
  data?: unknown;
}

export const PAYROLL_USER_HUB_EVENT = "payroll.user";
export interface PayrollUserHubEvent extends HubEvent {
  data: {
    user: ProtoPayrollUser<string>;
    payrollId: string;
  };
}
export const PAYROLL_HUB_EVENT = "payroll";
export interface PayrollHubEvent extends HubEvent {
  data: {
    payroll: ProtoPayroll<string>;
  };
}

export const ROTA_EVENT = "rota";
export const ROTA_SHIFT_EVENT = "shift";
export interface RotaShiftHubEvent extends HubEvent {
  data: {
    shift: unknown;
  };
}

export interface PiniaApiHelper {
  get: (api: string, url: string, opts?: any) => Promise<unknown>;
  post: (api: string, url: string, body?: unknown) => Promise<unknown>;
  put: (api: string, url: string, body?: unknown) => Promise<unknown>;
  patch: (api: string, url: string, body?: unknown) => Promise<unknown>;
  delete: (api: string, url: string) => Promise<unknown>;
  doNotCatchIfError: () => PiniaApiHelper;
}

export const RotaShiftAssignmentTypes = {
  CURRENT: "current",
  HOLIDAY: "holiday",
  SICK: "sick",
  OLD: "old",
  UNASSIGNED: "unassigned",
} as const;

export interface RotaAssignedTo<TId = string> {
  type: string;
  userId?: TId;
  agencyId?: TId;
  displayName?: string;
  startDate?: Date;
  endDate?: Date;
}

export interface RotaScheduleItem {
  startTime: string;
  endTime: string;
  type: string;
  notes?: string;
}

export interface RotaActivityLink {
  activityId: string;
}

export interface RotaShift<TId = string> {
  id: TId;
  startDate: Date;
  endDate: Date;
  notes?: string;
  assignedTo?: RotaAssignedTo<TId>[];
  coverNotRequired?: boolean;
  schedule?: RotaScheduleItem[];
  activities?: RotaActivityLink[];
}

export type RotaShiftWithEmbeddedActivities<TId = string> = Omit<
  RotaShift<TId>,
  "activities"
> & {
  activities?: RotaActivity<TId>[];
};

export interface Rota<TId = string> {
  id: TId;
  name?: string;
  startDate: Date;
  endDate: Date;
  status: (typeof RotaStatus)[keyof typeof RotaStatus];
  location: string;
  shifts?: RotaShift<TId>[];
}

export const RotaStatus = {
  DRAFT: "draft",
  PUBLISHED: "published",
  LOCKED: "locked",
  MASTER: "master",
} as const;

export type RotaActivity<TId = string> = {
  id: TId;
  dateCreated: Date;
  title: string;
  startDate: Date;
  endDate: Date;
  notes: string;
  type?: string; // should be present, but some old 'title = Support' entries might not have it
  personsRequired: number; // normally 1

  dutiesRequired?: string[];
  locationId?: string;
  master?: boolean;
  rotaId?: TId;

  dateModified?: Date;
  clients?: Client<TId>[];
  shifts?: {
    shiftId: TId;
    rotaId: TId;
    userId: TId;
    displayName: string;
  }[];
};

export type Client<TId = string> = {
  clientId: TId;
  initials: string;
  location: {
    locationId: string;
    roomNumber: string;
  };
};
